import React, { useState, useRef } from 'react'
import styled from '@emotion/styled'
import { colors } from '../../../../../constants'
import { Option, DateMenuTemplate } from '@types'
import TemplateForm from './TemplateForm'
import TemplatesList, { TemplatesListRef } from './TemplatesList'
import {
  CreateDateMenuTemplateReq,
  UpdateDateMenuTemplateReq,
  SearchDateMenuTemplatesReq,
} from '@coordinators/groupOrders/dateMenuTemplates'

interface Chef {
  id: string
  name: string
}

interface DateMenuTemplatesManagerProps {
  hqId: number
  userId: string
  userEmail: string
  cuisineOptions: Option[]
  loadChefs: (params: any) => Promise<Chef[]>
  displayFailureMessage: (message: string) => void
  displayInfoMessage: (message: string) => void
  displayConfirmationModal: ({
    title,
    text,
  }: {
    title: string
    text: string
  }) => Promise<boolean>
  createDateMenuTemplate: (
    req: CreateDateMenuTemplateReq,
  ) => Promise<DateMenuTemplate | null>
  updateDateMenuTemplate: (
    req: UpdateDateMenuTemplateReq,
  ) => Promise<DateMenuTemplate | null>
  searchDateMenuTemplates: (
    req: SearchDateMenuTemplatesReq,
  ) => Promise<DateMenuTemplate[] | null>
  deleteDateMenuTemplates: (id: string) => Promise<boolean>
  onSelectTemplate?: (template: DateMenuTemplate) => void
}

const DateMenuTemplatesManager: React.FC<DateMenuTemplatesManagerProps> = ({
  hqId,
  userId,
  userEmail,
  cuisineOptions,
  loadChefs,
  displayConfirmationModal,
  displayFailureMessage,
  displayInfoMessage,
  createDateMenuTemplate,
  updateDateMenuTemplate,
  searchDateMenuTemplates,
  deleteDateMenuTemplates,
  onSelectTemplate,
}) => {
  // State to track the currently selected template
  const [selectedTemplate, setSelectedTemplate] =
    useState<DateMenuTemplate | null>(null)

  // Ref to access TemplatesList methods
  const templatesListRef = useRef<TemplatesListRef>(null)

  // Handler for when a template is selected from the list
  const handleSelectTemplate = (template: DateMenuTemplate) => {
    setSelectedTemplate(template)
  }

  const refreshTemplates = async (template: DateMenuTemplate) => {
    // Determine which section needs to be refreshed based on template properties
    if (!templatesListRef.current) {
      return
    }

    try {
      // If template is private, refresh private templates
      if (template.isPrivate) {
        await templatesListRef.current.refreshPrivateTemplates()

        return
      }

      // If template has an hqId matching this market, refresh market templates
      if (template.hqId === hqId) {
        await templatesListRef.current.refreshMarketTemplates()

        return
      }

      // Otherwise, it's a global template
      await templatesListRef.current.refreshGlobalTemplates()
    } catch (error) {
      displayFailureMessage(`Failed to refresh templates: ${error}`)
    }
  }

  // Handler for when a template is created or updated from the form
  const handleTemplateUpdate = async (template: DateMenuTemplate) => {
    setSelectedTemplate(template)
    refreshTemplates(template)
  }

  const handleDeleteTemplate = async (template: DateMenuTemplate) => {
    if (!template.id) {
      displayFailureMessage('This template is not saved yet')

      return
    }

    const confirmed = await displayConfirmationModal({
      title: 'Delete Template',
      text: `Are you sure you want to delete template "${template.name}"?`,
    })

    if (confirmed) {
      await deleteDateMenuTemplates(template.id)
      displayInfoMessage('Template deleted successfully')
      setSelectedTemplate(null)
      refreshTemplates(template)
    }
  }

  return (
    <Container>
      <Header>Date Menu Templates Manager</Header>
      <ContentWrapper>
        <MainContent>
          {/* Render TemplatesList on the left */}
          <TemplatesList
            ref={templatesListRef}
            userId={userId}
            hqId={hqId}
            onSelectTemplate={handleSelectTemplate}
            searchDateMenuTemplates={searchDateMenuTemplates}
          />

          {/* Render TemplateForm on the right */}
          <TemplateForm
            hqId={hqId}
            userId={userId}
            userEmail={userEmail}
            cuisineOptions={cuisineOptions}
            loadChefs={loadChefs}
            handleTemplateUpdate={handleTemplateUpdate}
            displayFailureMessage={displayFailureMessage}
            displayInfoMessage={displayInfoMessage}
            createDateMenuTemplate={createDateMenuTemplate}
            updateDateMenuTemplate={updateDateMenuTemplate}
            initialTemplate={selectedTemplate || undefined}
            handleDeleteTemplate={handleDeleteTemplate}
            displayConfirmationModal={displayConfirmationModal}
            onSelectTemplate={onSelectTemplate}
          />
        </MainContent>
      </ContentWrapper>
    </Container>
  )
}

// Styled components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`

const Header = styled.div`
  padding: 16px 20px;
  font-size: 18px;
  font-weight: bold;
  border-bottom: 1px solid ${colors.gray200};
`

const ContentWrapper = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
`

const MainContent = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
`

export default DateMenuTemplatesManager
